import React, { useEffect } from 'react';
import {
  ChangePassword,
  FormFieldItem,
  ForgotPassword,
  Header,
  Login,
  StyleProvider,
  defaultBrandingData,
  i18nLocal,
} from '@relaynetwork/design-system';
import type { ProductGroup } from '@app/core/models/client';
import { Client as Styletron } from 'styletron-engine-atomic';
import { Provider as StyletronProvider } from 'styletron-react';

/**
 * Single, screen-parameterized preview for the Log In / Forgot / Change
 * password screens (mirrors the one-component approach of
 * verification-preview). The per-screen card bodies are no longer hand-rolled
 * here — they render the design system's `Login`, `ForgotPassword` and
 * `ChangePassword` organisms (added in @relaynetwork/design-system 1.1.49) so
 * the preview stays in lockstep with what The Feed actually renders. The
 * organisms only draw their `Card`; the preview still owns the shared chrome —
 * `Header`, branding, i18n init, wrapper and styletron engine. The organisms
 * render with `isMobile={false}` so their `Card` stays contained in the phone
 * mockup (the mobile layout pins a full-height, viewport-fixed button footer
 * that escapes the mockup). A known side effect: at `isMobile={false}` baseui's
 * viewport-keyed layout-grid wraps the Forgot/Change Cancel/Submit buttons onto
 * separate rows in the desktop-width preview. That's accepted for now — see
 * DIS-610. The "Create" screen keeps its own dedicated component
 * (SetPasswordPagePreview → DS `SetPassword`).
 *
 * Note: the Change screen intentionally no longer renders Terms & Conditions /
 * consent — the DS `ChangePassword` organism omits them (in The Feed those
 * belong to the Create/Set flow), so this preview no longer accepts the
 * `psw*`/`consentContent` props at all.
 */
export type PasswordPreviewScreen = 'login' | 'forgot' | 'change';

export type PasswordPagePreviewProps = {
  screen: PasswordPreviewScreen;
  logoUrl: ProductGroup['branding']['icon_s3_url'];
  color: ProductGroup['branding']['color'];
  showProductGroup: boolean;
  productGroupName: ProductGroup['name'];
  productGroupDescription: ProductGroup['description'];
  fdicSignage: ProductGroup['fdic'];
  showFdic: boolean;
  language?: string;
  showLogo?: boolean;
  showTitleBar?: boolean;
  /**
   * Renders the screen's error state: Log In shows the DS "mobile number or
   * password do not match" field error (the `Login` organism has no error
   * prop), Forgot renders its error card (`isError`) and Change renders the
   * change-password error banner (`isChangePasswordError`).
   */
  isError?: boolean;
};

const engine = new Styletron({ prefix: 'password-page-preview-' });

// Preview-only no-op for the organisms' submit/cancel handlers (all form
// interaction lives in The Feed containers, not the PDF preview).
const noop = (): void => undefined;

function buildFields(
  screen: PasswordPreviewScreen,
  language: string,
  isError: boolean,
): FormFieldItem[] {
  const es = language === 'es';
  const mobileNumber: FormFieldItem = {
    field: 'mobile_number',
    prompt: es ? 'Número de Móvil' : 'Mobile Number',
    type: 'tel',
    required: true,
    phoneNumberMinimumLength: true,
  };

  switch (screen) {
    case 'login':
      return [
        mobileNumber,
        {
          field: 'login_password',
          prompt: es ? 'Contraseña' : 'Password',
          type: 'password',
          required: true,
          // DS renders its (translated) default "Mobile number or password do
          // not match." text under the field when this flag is set.
          wrongPasswordOrNumberCheck: isError,
        },
      ];
    case 'forgot':
      return [mobileNumber];
    case 'change':
      return [
        {
          field: 'password',
          prompt: es ? 'Contraseña Actual' : 'Current Password',
          type: 'password',
          required: true,
        },
        {
          field: 'newPassword',
          prompt: es ? 'Nueva Contraseña' : 'New Password',
          type: 'password',
          alwaysShowHints: true,
          required: true,
          upperCaseRequired: true,
          lowerCaseRequired: true,
          numberRequired: true,
          minimumLength: 8,
          maximumLength: 256,
        },
        {
          field: 'confirmNewPassword',
          prompt: es ? 'Confirmar Contraseña' : 'Confirm Password',
          type: 'password',
          required: true,
          equalityCheckField: 'newPassword',
        },
      ];
  }
}

export default function PasswordPagePreview({
  screen,
  logoUrl,
  color,
  showProductGroup,
  productGroupName,
  productGroupDescription,
  fdicSignage,
  showFdic,
  language = 'en',
  showLogo = true,
  showTitleBar = true,
  isError = false,
}: PasswordPagePreviewProps) {
  const fields = buildFields(screen, language, isError);
  const brandingData = { ...defaultBrandingData, color };

  useEffect(() => {
    i18nLocal.init();
    i18nLocal.changeLanguage(language);

    return () => {
      i18nLocal.init();
      i18nLocal.changeLanguage('en');
    };
  }, []);

  const renderScreen = () => {
    switch (screen) {
      case 'login':
        return (
          <Login
            isMobile={false}
            fields={fields}
            onSubmit={noop}
            forgotPasswordHref="#"
            wireDisplayName={showProductGroup ? productGroupName : ''}
          />
        );
      case 'forgot':
        return (
          <ForgotPassword
            isMobile={false}
            fields={fields}
            onSubmit={noop}
            onClose={noop}
            isPreview={true}
            isError={isError}
          />
        );
      case 'change':
        return (
          <ChangePassword
            isMobile={false}
            fields={fields}
            onSubmit={noop}
            onClose={noop}
            forgotPasswordHref="#"
            isPreview={true}
            isChangePasswordError={isError}
          />
        );
    }
  };

  return (
    <StyletronProvider value={engine}>
      <StyleProvider brandingData={brandingData}>
        <Header
          productGroupDescription={productGroupDescription}
          productGroupName={showProductGroup ? productGroupName : ' '}
          showTitleNavBar={showTitleBar}
          logoUrl={logoUrl}
          showRegularNavBar
          showLogo={showLogo}
          showBanner={false}
          isPreview={true}
          isMobile
          isPortrait={true}
          fdic={fdicSignage}
          showFDICSignageOnPassword={showFdic}
        />
        <div
          style={{
            marginTop: '1rem',
            marginLeft: '0',
            marginRight: '0',
          }}
        >
          {renderScreen()}
        </div>
      </StyleProvider>
    </StyletronProvider>
  );
}
